/**
 * Vitals service — FHIR Observation-backed EMR endpoints.
 *
 * Replaces the retired `patient.weight` / `patient.height` /
 * `patient.blood_pressure_*` snapshot reads. Emits LOINC-coded
 * observation rows (category=vital-signs) on write.
 *
 * The backend controllers re-shape FHIR rows in three different ways
 * depending on the endpoint (camelCase, friendly-label-keyed, parsed
 * floats). `vitalFromApi` normalizes everything back to the FE's
 * snake_case `VitalObservation` interface so component code reads
 * `obs.value_quantity_value` consistently.
 */

import { api } from './api';

// Common LOINC codes the UI uses.
export const VitalLoinc = {
  BLOOD_PRESSURE_PANEL: '85354-9',
  SYSTOLIC_BP: '8480-6',
  DIASTOLIC_BP: '8462-4',
  HEART_RATE: '8867-4',
  RESPIRATORY_RATE: '9279-1',
  BODY_TEMPERATURE: '8310-5',
  OXYGEN_SATURATION: '2708-6',
  BODY_WEIGHT: '29463-7',
  BODY_HEIGHT: '8302-2',
  BMI: '39156-5',
  PAIN_SCALE: '72514-3',
} as const;

// Friendly labels the BE's `getLatestVitals` returns; we invert this
// map to re-key results by LOINC code (which is what FE consumers want).
const LABEL_TO_LOINC: Record<string, string> = {
  bloodPressure: VitalLoinc.BLOOD_PRESSURE_PANEL,
  heartRate: VitalLoinc.HEART_RATE,
  respiratoryRate: VitalLoinc.RESPIRATORY_RATE,
  temperature: VitalLoinc.BODY_TEMPERATURE,
  oxygenSaturation: VitalLoinc.OXYGEN_SATURATION,
  weight: VitalLoinc.BODY_WEIGHT,
  height: VitalLoinc.BODY_HEIGHT,
  bmi: VitalLoinc.BMI,
};

export interface VitalObservation {
  id: string;
  patient_id: string;
  encounter_id?: string | null;
  clinic_id: string;
  status: string;
  category_code: string;
  code_system: string;
  code_code: string;
  code_display: string;
  value_quantity_value?: number | string | null;
  value_quantity_unit?: string | null;
  interpretation_code?: string | null;
  effective_date_time?: string | null;
  components?: Array<{
    code: string;
    display: string;
    value?: number;
    unit?: string;
  }>;
}

export type BpPosition = 'sitting' | 'standing' | 'supine';
export type TempSite = 'oral' | 'tympanic' | 'temporal' | 'axillary' | 'rectal';

export interface RecordVitalsPayload {
  patientId: string;
  bloodPressureSystolic?: number;
  bloodPressureDiastolic?: number;
  heartRate?: number;
  respiratoryRate?: number;
  temperature?: number;
  temperatureUnit?: 'F' | 'C';
  oxygenSaturation?: number;
  weight?: number;
  weightUnit?: 'lb' | 'kg';
  height?: number;
  heightUnit?: 'in' | 'cm';
  painScale?: number;
  note?: string;
  effectiveDateTime?: string;
  bpPosition?: BpPosition;
  tempSite?: TempSite;
}

/**
 * Map a single observation as returned by any vitals read endpoint
 * (which all use camelCase-shaped output) to the FE's snake_case shape.
 */
const vitalFromApi = (obs: any): VitalObservation => ({
  id: obs?.id,
  patient_id: obs?.patient_id ?? obs?.patientId ?? '',
  encounter_id: obs?.encounter_id ?? obs?.encounterId ?? null,
  clinic_id: obs?.clinic_id ?? obs?.clinicId ?? '',
  status: obs?.status ?? 'final',
  category_code: obs?.category_code ?? 'vital-signs',
  code_system: obs?.code_system ?? 'http://loinc.org',
  code_code: obs?.code_code ?? obs?.code ?? '',
  code_display: obs?.code_display ?? obs?.display ?? '',
  value_quantity_value:
    obs?.value_quantity_value ?? obs?.value ?? null,
  value_quantity_unit:
    obs?.value_quantity_unit ?? obs?.unit ?? null,
  interpretation_code:
    obs?.interpretation_code ?? obs?.interpretation ?? null,
  effective_date_time:
    obs?.effective_date_time ?? obs?.effectiveDateTime ?? null,
  components: Array.isArray(obs?.components) ? obs.components : undefined,
});

export const vitalsService = {
  async recordVitals(
    clinicId: string,
    encounterId: string,
    payload: RecordVitalsPayload
  ): Promise<{ observations: VitalObservation[]; count: number }> {
    const response = await api.post(
      `/emr/${clinicId}/encounters/${encounterId}/vitals`,
      payload,
    );
    const data = response.data || response || {};
    return {
      observations: Array.isArray(data.observations)
        ? data.observations.map(vitalFromApi)
        : [],
      count: data.count ?? 0,
    };
  },

  /**
   * Patient-scoped vitals write. Used by Patient Overview / Patient Form
   * surfaces where there is no active encounter (chart correction or quick
   * update outside of a visit). Server records observations with
   * `off_encounter_reason='chart_correction'`.
   */
  async recordPatientVitals(
    clinicId: string,
    patientId: string,
    payload: RecordVitalsPayload,
  ): Promise<{ observations: VitalObservation[]; count: number }> {
    const response = await api.post(
      `/emr/${clinicId}/patients/${patientId}/vitals`,
      { ...payload, off_encounter_reason: 'chart_correction' },
    );
    const data = response.data || response || {};
    return {
      observations: Array.isArray(data.observations)
        ? data.observations.map(vitalFromApi)
        : [],
      count: data.count ?? 0,
    };
  },

  async getEncounterVitals(clinicId: string, encounterId: string): Promise<VitalObservation[]> {
    const response = await api.get(`/emr/${clinicId}/encounters/${encounterId}/vitals`);
    // BE returns `data: { vitals: { [loinc]: [obs, …] } }`. Flatten.
    const grouped = response.data?.vitals || {};
    const flat: VitalObservation[] = [];
    for (const arr of Object.values(grouped)) {
      if (Array.isArray(arr)) {
        for (const obs of arr) flat.push(vitalFromApi(obs));
      }
    }
    return flat;
  },

  async getPatientVitals(
    clinicId: string,
    patientId: string,
    options?: { limit?: number }
  ): Promise<VitalObservation[]> {
    const params = new URLSearchParams();
    if (options?.limit) params.append('limit', String(options.limit));
    const qs = params.toString();
    const response = await api.get(
      `/emr/${clinicId}/patients/${patientId}/vitals${qs ? `?${qs}` : ''}`,
    );
    const rows = response.data?.observations || [];
    return rows.map(vitalFromApi);
  },

  /**
   * Returns a map keyed by LOINC code — what FE consumers want.
   * The BE's `getLatestVitals` actually keys by friendly label
   * (`bloodPressure`, `heartRate`); we invert the keys here and also
   * surface the BP panel's components as `[SYSTOLIC_BP]` / `[DIASTOLIC_BP]`
   * pseudo-rows so legacy callers reading those LOINCs keep working.
   */
  async getLatestVitals(
    clinicId: string,
    patientId: string,
  ): Promise<Record<string, VitalObservation | null>> {
    const response = await api.get(`/emr/${clinicId}/patients/${patientId}/vitals/latest`);
    const labelKeyed = response.data?.vitals || {};
    const out: Record<string, VitalObservation | null> = {};

    for (const [label, raw] of Object.entries<any>(labelKeyed)) {
      const loinc = LABEL_TO_LOINC[label] || label;
      out[loinc] = raw ? vitalFromApi(raw) : null;
    }

    // Surface the systolic/diastolic components from the BP panel so
    // PatientOverviewTab's reads of [SYSTOLIC_BP] / [DIASTOLIC_BP] work.
    const bp = out[VitalLoinc.BLOOD_PRESSURE_PANEL];
    if (bp?.components?.length) {
      for (const c of bp.components) {
        if (!c.code) continue;
        out[c.code] = {
          ...bp,
          code_code: c.code,
          code_display: c.display ?? bp.code_display,
          value_quantity_value: c.value ?? null,
          value_quantity_unit: c.unit ?? null,
          components: undefined,
        };
      }
    }

    return out;
  },

  async getVitalTrend(
    clinicId: string,
    patientId: string,
    loincCode: string,
    limit = 10,
  ): Promise<VitalObservation[]> {
    const params = new URLSearchParams({ limit: String(limit) });
    const response = await api.get(
      `/emr/${clinicId}/patients/${patientId}/vitals/trend/${loincCode}?${params.toString()}`,
    );
    // BE returns `data: { trend: [...] }`.
    const rows = response.data?.trend || [];
    return rows.map(vitalFromApi);
  },
};

/**
 * Convenience: pluck a numeric reading in lb/cm/mmHg for a single LOINC.
 * Returns null when no observation exists.
 */
export const readVitalNumber = (obs?: VitalObservation | null): number | null => {
  if (!obs || obs.value_quantity_value == null) return null;
  const n = typeof obs.value_quantity_value === 'string'
    ? parseFloat(obs.value_quantity_value)
    : obs.value_quantity_value;
  return Number.isFinite(n) ? n : null;
};

export default vitalsService;
